import { Button } from '@/components/shadcn/ui/button';
import {
  mapFormDataToSignal,
  mapSignalToFormData,
  SignalAlertFormContent,
  signalAlertFormOptions,
  useUpdateSignalAlertMutation,
} from '@/entities/notification';
import { errorHandler } from '@/error-handler';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useAppForm } from '@/shared/ui/form';
import { toast } from '@/stores/pure/ToastStore';
import { WORKFLOW_TYPE } from '@/types/workflowConst';

import { useSignalAlertTitles } from '../hooks/use-signal-alert-titles';

export type UpdateSignalAlertDialogProps = {
  isOpen: boolean;
  close: () => void;
  unmount: () => void;
  alert: SignalAlertWorkflow;
  workspaceId: string;
};

export function UpdateSignalAlertDialog({
  isOpen,
  close,
  unmount,
  alert,
  workspaceId,
}: UpdateSignalAlertDialogProps) {
  const updateMutation = useUpdateSignalAlertMutation();
  // 현재 alert의 alias를 제외하여 중복 검사
  const { existingTitles } = useSignalAlertTitles({
    excludeAlias: alert.alias,
  });

  const form = useAppForm({
    ...signalAlertFormOptions,
    defaultValues: {
      ...mapSignalToFormData(alert.signal, alert.channel),
      title: alert.title,
    },
    onSubmit: async ({ value }) => {
      try {
        await updateMutation.mutateAsync({
          notification_id: alert.alias,
          title: value.title,
          enable: alert.enable,
          type: WORKFLOW_TYPE.SIGNAL_ALERT,
          signal: mapFormDataToSignal(value),
          channel: value.channel,
        });

        toast('Signal alert updated', 'success');
        close();
      } catch (error) {
        toast(errorHandler(error, 'Failed to update signal alert'), 'fail');
      }
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={() => close()} onExitComplete={unmount}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>Edit signal alert</DialogTitle>
          <DialogDescription>
            Get notified when unusual issues appear on the list.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex-1 min-h-0">
          <SignalAlertFormContent
            form={form}
            workspaceId={workspaceId}
            existingTitles={existingTitles}
            renderFooter={undefined}
          />
        </DialogBody>

        <DialogFooter>
          <div />
          <div className="flex gap-2">
            <DialogClose>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button
                  variant="default"
                  disabled={!canSubmit || isSubmitting}
                  onClick={form.handleSubmit}
                >
                  Save
                </Button>
              )}
            </form.Subscribe>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
